import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Popconfirm,
  Rate,
  Space,
  Table,
} from 'antd';
import { ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { axios } from 'lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';
import { fmtDate } from 'lib/adminDetail';

const FeedbackPage = () => {
  const { message } = App.useApp();
  const colors = useBrandToken();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/feedback', { params: { limit: 100 } });
      const list = res?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((f) =>
      `${f.comment || ''} ${f.review || ''} ${f.outlet?.name || ''} ${
        f.user?.full_name || ''
      } ${f.user?.mobile_number || ''}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const remove = async (id) => {
    try {
      await axios.delete(`/feedback/${id}`);
      message.success('Feedback removed');
      if (selected?._id === id) setSelected(null);
      fetchFeedback();
    } catch (e) {
      // toast via interceptor
    }
  };

  const columns = [
    {
      title: 'Outlet',
      key: 'outlet',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <strong>{r.outlet?.name || '—'}</strong>
          <span style={{ color: colors.muted, fontSize: 12 }}>
            {r.outlet?.city || r.outlet?.address || ''}
          </span>
        </Space>
      ),
    },
    {
      title: 'Customer',
      key: 'user',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <span>{r.user?.full_name || '—'}</span>
          <span style={{ color: colors.muted, fontSize: 12 }}>
            {r.user?.mobile_number || r.user?.email || ''}
          </span>
        </Space>
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      render: (v) => <Rate disabled allowHalf value={Number(v) || 0} />,
    },
    {
      title: 'Review',
      key: 'comment',
      ellipsis: true,
      render: (_, r) => r.comment || r.review || '—',
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => fmtDate(v),
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button type="primary" size="small" onClick={() => setSelected(record)}>
            View
          </Button>
          <Popconfirm
            title="Delete this feedback?"
            onConfirm={() => remove(record._id)}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageShell
      title="Feedback & ratings"
      description="Full customer reviews, ratings, and store/customer contact."
      actions={
        <Button icon={<ReloadOutlined />} onClick={fetchFeedback} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Card style={{ borderRadius: 12, border: `1px solid ${colors.border}` }}>
        <Input.Search
          placeholder="Search outlet, customer, review"
          allowClear
          style={{ maxWidth: 360, marginBottom: 16 }}
          onSearch={setSearch}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Table
          rowKey={(r) => r._id}
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          onRow={(record) => ({
            onClick: () => setSelected(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title="Feedback details"
        open={!!selected}
        onCancel={() => setSelected(null)}
        footer={[
          selected ? (
            <Popconfirm
              key="del"
              title="Delete this feedback?"
              onConfirm={() => remove(selected._id)}
            >
              <Button danger>Delete</Button>
            </Popconfirm>
          ) : null,
          <Button key="close" onClick={() => setSelected(null)}>
            Close
          </Button>,
        ]}
        width={640}
      >
        {selected && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Outlet">
              {selected.outlet?.name || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Outlet address">
              {[selected.outlet?.address, selected.outlet?.city]
                .filter(Boolean)
                .join(', ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Customer">
              {selected.user?.full_name || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Customer contact">
              {selected.user?.mobile_number || selected.user?.email || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Rating">
              <Rate disabled allowHalf value={Number(selected.rating) || 0} />
            </Descriptions.Item>
            <Descriptions.Item label="Review">
              {selected.comment || selected.review || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Submitted">
              {fmtDate(selected.createdAt)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </PageShell>
  );
};

export default FeedbackPage;
